import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { useT } from '../i18n/I18nContext';
import { trackEvent } from '../analytics';
import { useTrackSection } from '../hooks/useTrackSection';

// ── Icons ────────────────────────────────────────────────────────────────────

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="7" width="10" height="8" rx="2" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" />
    <path d="M5 7V5a3 3 0 016 0v2" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="3" width="14" height="10" rx="2" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" />
    <path d="M1 5l7 5 7-5" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" />
    <path d="M8 5v3.5l2.5 1.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const WaterDropIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
    <path d="M9 2C9 2 3.5 8 3.5 12a5.5 5.5 0 0011 0C14.5 8 9 2 9 2z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);
const ActivesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="2.2" fill="white" opacity="0.85" />
    <circle cx="3.5" cy="4.5" r="1.6" fill="white" opacity="0.7" />
    <circle cx="14.5" cy="4.5" r="1.6" fill="white" opacity="0.7" />
    <circle cx="9" cy="15" r="1.6" fill="white" opacity="0.7" />
    <line x1="9" y1="6.8" x2="4.8" y2="5.6" stroke="white" strokeWidth="1.1" opacity="0.65" />
    <line x1="9" y1="6.8" x2="13.2" y2="5.6" stroke="white" strokeWidth="1.1" opacity="0.65" />
    <line x1="9" y1="11.2" x2="9" y2="13.4" stroke="white" strokeWidth="1.1" opacity="0.65" />
  </svg>
);
const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="3.5" stroke="white" strokeWidth="1.6" />
    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.1 4.1l1.4 1.4M14.5 14.5l1.4 1.4M14.5 5.5l-1.4 1.4M5.5 14.5l-1.4 1.4" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const StepIconCleanser = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M7 16V8c0-1 .5-2 1.5-2.5L10 5l1.5.5C12.5 6 13 7 13 8v8" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 16h8" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 5V3.5C9 3 9.4 3 10 3s1 0 1 .5V5" stroke="#7C5CB8" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const StepIconToner = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <rect x="6" y="5" width="8" height="11" rx="2" stroke="#7C5CB8" strokeWidth="1.4" />
    <path d="M8 5V3.5C8 3 8.5 3 10 3s2 0 2 .5V5" stroke="#7C5CB8" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M8.5 9.5h3M8.5 12h3" stroke="#7C5CB8" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);
const StepIconSerum = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M10 3v2M10 5c-2 0-3.5 2-3.5 4.5V15a1.5 1.5 0 003 0v-1h1v1a1.5 1.5 0 003 0V9.5C13.5 7 12 5 10 5z" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 3h2" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const StepIconCream = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <rect x="4" y="9" width="12" height="8" rx="2" stroke="#7C5CB8" strokeWidth="1.4" />
    <path d="M4 11c2-2 10-2 12 0" stroke="#7C5CB8" strokeWidth="1.3" />
    <path d="M7 9V7.5C7 6 8 5 10 5s3 1 3 2.5V9" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const StepIconSpf = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M8 16V7c0-1 .5-1.5 1-1.5h2c.5 0 1 .5 1 1.5v1h1c.5 0 1 .5 1 1v5.5c0 1-.5 1.5-1 1.5H7c-.5 0-1-.5-1-1.5V10c0-.5.5-1 1-1h1" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 4V3" stroke="#7C5CB8" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// ── Card organic layout params ────────────────────────────────────────────────

const CARD_ORGANIC = [
  { rotation: -1.8, x: -4, y: -6  },
  { rotation:  1.6, x:  5, y:  5  },
  { rotation:  0.9, x: -3, y:  7  },
  { rotation: -2.1, x:  4, y: -4  },
];

// ── Component ────────────────────────────────────────────────────────────────

export default function HeroSection() {
  const sectionRef   = useRef<HTMLElement>(null);
  const bgOverlayRef = useRef<HTMLDivElement>(null);
  const badgeRef     = useRef<HTMLDivElement>(null);
  const headlineRef  = useRef<HTMLHeadingElement>(null);
  const subRef       = useRef<HTMLParagraphElement>(null);
  const ctaRef       = useRef<HTMLDivElement>(null);
  const trustRef     = useRef<HTMLDivElement>(null);
  const badgesRef    = useRef<HTMLDivElement>(null);
  const card1Ref     = useRef<HTMLDivElement>(null);
  const card2Ref     = useRef<HTMLDivElement>(null);
  const card3Ref     = useRef<HTMLDivElement>(null);
  const card4Ref     = useRef<HTMLDivElement>(null);

  const cardRefs = [card1Ref, card2Ref, card3Ref, card4Ref];
  const t = useT();
  useTrackSection('hero', sectionRef);

  // ── Entrance animation ────────────────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      cardRefs.forEach((ref, i) => {
        const o = CARD_ORGANIC[i];
        gsap.set(ref.current, { rotation: o.rotation, x: o.x, y: o.y + 32, opacity: 0, scale: 0.92 });
      });

      const tl = gsap.timeline({ defaults: { ease: 'power2.out' }, delay: 0.15 });

      tl.fromTo(badgeRef.current,
        { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.65 })
        .fromTo(headlineRef.current?.querySelectorAll('span') || [],
          { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 0.72, stagger: 0.11 }, '-=0.28')
        .fromTo(subRef.current,
          { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.62 }, '-=0.32')
        .fromTo(ctaRef.current,
          { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.58 }, '-=0.28')
        .fromTo(trustRef.current,
          { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.48 }, '-=0.18')
        .fromTo(badgesRef.current,
          { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.42 }, '-=0.14');

      cardRefs.forEach((ref, i) => {
        const o = CARD_ORGANIC[i];
        gsap.to(ref.current, {
          opacity: 1, y: o.y, scale: 1,
          duration: 0.55, ease: 'back.out(1.2)',
          delay: 0.18 + i * 0.08,
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Card hover spread ─────────────────────────────────────────────────────
  const handleCardEnter = (index: number) => {
    const hRow = Math.floor(index / 2);
    const hCol = index % 2;
    cardRefs.forEach((ref, i) => {
      const o = CARD_ORGANIC[i];
      if (i === index) {
        gsap.to(ref.current, { y: o.y - 9, scale: 1.04, boxShadow: '0 40px 100px rgba(100,70,160,0.22)', duration: 0.38, ease: 'power2.out' });
      } else {
        const dx = (i % 2 - hCol) * 9;
        const dy = (Math.floor(i / 2) - hRow) * 8;
        gsap.to(ref.current, { x: o.x + dx, y: o.y + dy, duration: 0.38, ease: 'power2.out' });
      }
    });
  };

  const handleCardLeave = () => {
    cardRefs.forEach((ref, i) => {
      const o = CARD_ORGANIC[i];
      gsap.to(ref.current, { x: o.x, y: o.y, scale: 1, boxShadow: '0 30px 90px rgba(100,70,140,0.14)', duration: 0.52, ease: 'power2.out' });
    });
  };

  // ── Shared card style ─────────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.76)',
    backdropFilter: 'blur(32px)',
    WebkitBackdropFilter: 'blur(32px)',
    border: '1px solid rgba(255, 255, 255, 0.82)',
    borderRadius: 28,
    boxShadow: '0 16px 52px rgba(80, 40, 180, 0.18), 0 2px 8px rgba(160, 100, 255, 0.10), 0 1px 0 rgba(255,255,255,0.8) inset',
    cursor: 'default',
    willChange: 'transform',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section
      ref={sectionRef}
      id="hero"
      className="hero-section"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div ref={bgOverlayRef} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }} />

      {/* Content grid */}
      <div
        className="hero-grid"
        style={{
          display: 'grid',
          maxWidth: 1440,
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* ── Left column ──────────────────────────────────────────────── */}
        <div style={{ minWidth: 0 }}>
          <div
            ref={badgeRef}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              maxWidth: '100%', flexWrap: 'wrap',
              background: 'rgba(255,255,255,0.16)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.32)',
              borderRadius: 20, padding: '7px 18px 7px 12px',
              marginBottom: 32, opacity: 0,
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'rgba(255,255,255,0.75)',
              display: 'inline-block', flexShrink: 0,
              animation: 'heroBadgePulse 2.4s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.88)', letterSpacing: '0.04em' }}>
              {t.hero.badge}
            </span>
          </div>

          <h1
            ref={headlineRef}
            style={{
              fontSize: 'clamp(28px, 4.2vw, 58px)',
              fontWeight: 800, lineHeight: 1.14,
              letterSpacing: '-0.025em', marginBottom: 26, color: 'white',
              overflowWrap: 'break-word', wordBreak: 'break-word',
            }}
          >
            {t.hero.headline.map((line, i) => (
              <span key={i} style={{ display: 'block', opacity: 0 }}>{line}</span>
            ))}
          </h1>

          <p
            ref={subRef}
            className="hero-subhead"
            style={{
              fontSize: 'clamp(14px, 1.5vw, 16px)', lineHeight: 1.72,
              color: 'rgba(255,255,255,0.78)',
              marginBottom: 36, maxWidth: 460,
              opacity: 0, fontWeight: 400,
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {t.hero.subhead}
          </p>

          <div ref={ctaRef} className="hero-cta-row" style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 34, flexWrap: 'wrap', opacity: 0 }}>
            <Link
              to="/get-started"
              onClick={() => trackEvent('cta_clicked', { location: 'hero', label: 'primary' })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 9,
                background: 'linear-gradient(135deg, #F36BAF 0%, #8B6CFF 100%)',
                color: 'white', border: 'none', borderRadius: 100,
                padding: '15px 32px', fontFamily: 'Manrope, sans-serif',
                fontWeight: 700, fontSize: 16, cursor: 'pointer',
                textDecoration: 'none',
                boxShadow: '0 8px 32px rgba(243,107,175,0.42)',
                transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                letterSpacing: '-0.01em',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 44px rgba(243,107,175,0.55)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(243,107,175,0.42)'; }}
            >
              {t.hero.ctaPrimary}
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <Link
              to="/example"
              onClick={() => trackEvent('cta_clicked', { location: 'hero', label: 'see_example' })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.14)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                color: 'white', border: '1.5px solid rgba(255,255,255,0.48)',
                borderRadius: 100, padding: '14px 28px',
                fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 15,
                cursor: 'pointer', transition: 'background 0.22s ease, border-color 0.22s ease',
                letterSpacing: '-0.01em',
                textDecoration: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.68)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.48)'; }}
            >
              {t.hero.ctaSecondary}
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          <div ref={trustRef} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, opacity: 0 }}>
            <div style={{ display: 'flex', gap: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} width="17" height="17" viewBox="0 0 14 14" fill="#FFBA08">
                  <path d="M7 1l1.545 3.09L12 4.636l-2.5 2.455.59 3.455L7 9l-3.09 1.546.59-3.455L2 4.636l3.455-.546L7 1z" />
                </svg>
              ))}
            </div>
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.84)', fontWeight: 500 }}>
              {t.hero.trustCount}
            </span>
          </div>

          <div ref={badgesRef} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 6, opacity: 0 }}>
            {[
              { Icon: LockIcon, text: t.hero.badges.private },
              { Icon: MailIcon, text: t.hero.badges.email },
              { Icon: ClockIcon, text: t.hero.badges.time },
            ].map(({ Icon, text }, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ color: 'rgba(255,255,255,0.28)', margin: '0 10px' }}>|</span>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon />
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.62)', fontWeight: 500 }}>{text}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Right column — cards ──────────────────────────────────────── */}
        <div className="hero-right-col" style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', minWidth: 0 }}>
          <div
            className="hero-cards-grid"
            onMouseLeave={handleCardLeave}
            style={{
              display: 'grid',
              gridTemplateRows: 'auto auto',
              gap: 20,
              width: '100%',
              maxWidth: 560,
              minWidth: 0,
            }}
          >
            {/* Card 1 — Skin type */}
            <div
              ref={card1Ref}
              style={{ ...cardStyle, padding: '26px 24px', opacity: 0 }}
              onMouseEnter={() => handleCardEnter(0)}
            >
              <div className="hero-card-head" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div className="hero-card-icon" style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: 'linear-gradient(135deg, #F472B6 0%, #A78BFA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <WaterDropIcon />
                </div>
                <span className="hero-card-label" style={{ fontSize: 11, fontWeight: 800, color: '#7C5CB8', textTransform: 'uppercase', letterSpacing: '0.10em' }}>{t.hero.cards.skinType.label}</span>
              </div>
              <p className="hero-card-value" style={{ fontSize: 'clamp(17px, 1.5vw, 21px)', fontWeight: 800, color: '#2D1B5A', lineHeight: 1.3, marginBottom: 20, whiteSpace: 'pre-line', overflowWrap: 'break-word' }}>
                {t.hero.cards.skinType.value}
              </p>
              <div style={{ height: 6, borderRadius: 6, background: 'rgba(180,148,220,0.16)', overflow: 'hidden' }}>
                <div style={{ width: '74%', height: '100%', background: 'linear-gradient(90deg, #F36BAF 0%, #8B6CFF 100%)', borderRadius: 6 }} />
              </div>
            </div>

            {/* Card 2 — Actives */}
            <div
              ref={card2Ref}
              style={{ ...cardStyle, padding: '24px 20px', opacity: 0 }}
              onMouseEnter={() => handleCardEnter(1)}
            >
              <div className="hero-card-head" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div className="hero-card-icon" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: 'linear-gradient(135deg, #F9A8D4 0%, #A78BFA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ActivesIcon />
                </div>
                <span className="hero-card-label" style={{ fontSize: 11, fontWeight: 800, color: '#7C5CB8', textTransform: 'uppercase', letterSpacing: '0.10em' }}>{t.hero.cards.actives.label}</span>
              </div>
              {t.hero.cards.actives.items.map((name, i) => {
                const color = ['#F36BAF', '#8B6CFF', '#60A5FA'][i] || '#8B6CFF';
                const item = { name, color };
                return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 2 ? 10 : 0 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                  <span className="hero-card-item" style={{ fontSize: 13, fontWeight: 600, color: '#2D1B5A', overflowWrap: 'break-word', minWidth: 0 }}>{item.name}</span>
                </div>
                );
              })}
            </div>

            {/* Card 3 — Care plan */}
            <div
              ref={card3Ref}
              style={{ ...cardStyle, padding: '24px 24px', opacity: 0 }}
              onMouseEnter={() => handleCardEnter(2)}
            >
              <div className="hero-card-head" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div className="hero-card-icon" style={{ width: 42, height: 42, borderRadius: 13, flexShrink: 0, background: 'linear-gradient(135deg, #FCD34D 0%, #F472B6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SunIcon />
                </div>
                <span className="hero-card-label" style={{ fontSize: 11, fontWeight: 800, color: '#7C5CB8', textTransform: 'uppercase', letterSpacing: '0.10em' }}>{t.hero.cards.plan.label}</span>
              </div>
              <p className="hero-card-body" style={{ fontSize: 14, fontWeight: 600, color: '#2D1B5A', marginBottom: 16, lineHeight: 1.4 }}>
                {t.hero.cards.plan.text}
              </p>
              <div className="hero-step-row" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {[StepIconCleanser, StepIconToner, StepIconSerum, StepIconCream, StepIconSpf].map((Icon, i) => (
                  <div key={i} className="hero-step-icon" style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: 'rgba(130,90,210,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon />
                  </div>
                ))}
              </div>
            </div>

            {/* Card 4 — Report */}
            <div
              ref={card4Ref}
              style={{ ...cardStyle, padding: '24px 20px', opacity: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}
              onMouseEnter={() => handleCardEnter(3)}
            >
              <div style={{ position: 'relative', width: 74, height: 74, marginBottom: 12 }}>
                <svg width="74" height="74" viewBox="0 0 74 74">
                  <circle cx="37" cy="37" r="29" stroke="rgba(180,150,220,0.18)" strokeWidth="5.5" fill="none" />
                  <circle
                    cx="37" cy="37" r="29" stroke="url(#heroRingGrd)"
                    strokeWidth="5.5" fill="none"
                    strokeDasharray="182.2" strokeDashoffset="23.1"
                    strokeLinecap="round" transform="rotate(-90 37 37)"
                    style={{ animation: 'heroRingFill 2.4s ease-out 1.6s both' }}
                  />
                  <defs>
                    <linearGradient id="heroRingGrd" x1="0" y1="0" x2="1" y2="0">
                      <stop stopColor="#F36BAF" /><stop offset="1" stopColor="#8B6CFF" />
                    </linearGradient>
                  </defs>
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#2D1B5A' }}>87%</div>
              </div>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#2D1B5A', marginBottom: 5 }}>{t.hero.cards.report.title}</p>
              <p style={{ fontSize: 13, color: '#7C5CB8', fontWeight: 500 }}>{t.hero.cards.report.status}</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes heroBadgePulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.45); opacity: 0.6; }
        }
        @keyframes heroRingFill {
          from { stroke-dashoffset: 182.2; }
          to   { stroke-dashoffset: 23.1; }
        }
        /* Desktop */
        #hero {
          align-items: center;
          overflow: visible;
          padding: 100px clamp(24px, 5vw, 80px) 60px;
        }
        .hero-grid {
          grid-template-columns: 45% 55%;
          gap: clamp(24px, 4vw, 56px);
          align-items: center;
        }
        .hero-cards-grid { grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr); }
        .hero-cards-grid > div { min-width: 0; }
        @media (max-width: 1100px) {
          .hero-cards-grid { max-width: 100%; gap: 14px; }
        }
        /* Tablet / mobile */
        @media (max-width: 860px) {
          #hero {
            align-items: flex-start;
            overflow: hidden;
            padding: 128px clamp(20px, 5vw, 40px) 48px;
          }
          .hero-grid { grid-template-columns: 1fr; gap: 28px; align-items: start; }
          .hero-cards-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            max-width: 100%;
            margin: 0 auto;
          }
          .hero-cta-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .hero-cta-row a {
            justify-content: center;
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .hero-subhead {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
          .hero-step-icon {
            width: 24px !important;
            height: 24px !important;
          }
          .hero-step-icon svg { width: 13px; height: 13px; }
          .hero-step-row { gap: 5px !important; }
          /* Compact card content on mobile */
          .hero-cards-grid > div { padding: 18px 14px !important; }
          .hero-cards-grid .hero-card-icon {
            width: 32px !important;
            height: 32px !important;
            border-radius: 9px !important;
          }
          .hero-cards-grid .hero-card-icon svg { width: 14px; height: 14px; }
          .hero-cards-grid .hero-card-head { gap: 8px !important; margin-bottom: 12px !important; }
          .hero-cards-grid .hero-card-label {
            font-size: 10px !important;
            letter-spacing: 0.08em !important;
          }
          .hero-cards-grid .hero-card-value {
            font-size: 15px !important;
            line-height: 1.25 !important;
            margin-bottom: 14px !important;
          }
          .hero-cards-grid .hero-card-body {
            font-size: 12px !important;
            margin-bottom: 12px !important;
          }
          .hero-cards-grid .hero-card-item { font-size: 12px !important; }
        }
        @media (max-width: 520px) {
          #hero { padding-top: 120px; }
          .hero-cards-grid { gap: 12px; }
          .hero-cards-grid > div:last-child { display: none; }
        }
      `}</style>
    </section>
  );
}
